import React, { useMemo } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Trophy, MapPin, Calendar, Users, Globe, Star, ExternalLink } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { useTeamDetails, useFavouriteTeams } from '../../hooks/useSports'
import { cn } from '../../lib/utils'

function fmtKickoff(iso, locale) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    })
  } catch {
    return iso
  }
}

// Football-data's `section` field on a squad row is the player's
// position group ("Goalkeeper", "Defence", "Midfield", "Offence").
// Group the squad into those four buckets in canonical order so the
// list reads top-to-bottom like a teamsheet.
const SECTION_ORDER = ['Goalkeeper', 'Defence', 'Midfield', 'Offence']
const SECTION_LABELS_EN = {
  Goalkeeper: 'Goalkeepers',
  Defence: 'Defenders',
  Midfield: 'Midfielders',
  Offence: 'Forwards',
}
const SECTION_LABELS_AR = {
  Goalkeeper: 'حراس المرمى',
  Defence: 'دفاع',
  Midfield: 'وسط',
  Offence: 'هجوم',
}

export function SportsTeamPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { teamId } = useParams()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const isArabic = locale === 'ar'

  const { data, loading, error } = useTeamDetails(teamId)
  const favTeams = useFavouriteTeams()

  const team = data?.team
  const matches = data?.matches || []

  // Split matches into "last" and "upcoming" so the page renders the
  // two as distinct sections. Football-data sorts by date asc by
  // default — the divide is anything FINISHED in "last", anything
  // else in "upcoming".
  const { lastMatches, upcomingMatches } = useMemo(() => {
    const last = []
    const next = []
    for (const m of matches) {
      if (m.status === 'FINISHED' || m.status === 'AWARDED') last.push(m)
      else next.push(m)
    }
    // Recent finished — show most recent first (last 5).
    last.sort((a, b) => new Date(b.utcDate) - new Date(a.utcDate))
    next.sort((a, b) => new Date(a.utcDate) - new Date(b.utcDate))
    return { lastMatches: last.slice(0, 5), upcomingMatches: next.slice(0, 5) }
  }, [matches])

  const groupedSquad = useMemo(() => {
    if (!Array.isArray(team?.squad)) return []
    const buckets = new Map()
    for (const p of team.squad) {
      const key = p.section || 'Other'
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key).push(p)
    }
    // Canonical order first; then anything else ("Other", coach
    // staff that slipped in, etc.) at the end.
    const ordered = []
    for (const k of SECTION_ORDER) {
      if (buckets.has(k)) ordered.push({ section: k, players: buckets.get(k) })
    }
    for (const [k, players] of buckets.entries()) {
      if (!SECTION_ORDER.includes(k)) ordered.push({ section: k, players })
    }
    return ordered
  }, [team?.squad])

  const isFavourite = team ? favTeams.teamIds.has(team.id) : false
  const sectionLabel = (sec) => (isArabic ? SECTION_LABELS_AR[sec] : SECTION_LABELS_EN[sec]) || sec

  return (
    <div className="space-y-3 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5" dir="auto">
            <Trophy className="w-4 h-4 text-app-primary-500" />
            {t('sports.team.title')}
          </h1>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-3 text-xs text-red-600" dir="auto">{error}</CardContent>
        </Card>
      ) : !team ? (
        <EmptyState icon={Trophy} title={t('sports.team.notFound')} />
      ) : (
        <>
          {/* Hero */}
          <Card className="overflow-hidden">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                {team.crest && (
                  <img
                    src={team.crest}
                    alt=""
                    loading="eager"
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-foreground" dir="auto">
                    {team.name}
                  </h2>
                  {team.shortName && team.shortName !== team.name && (
                    <p className="text-xs text-muted-foreground" dir="auto">{team.shortName}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px] text-muted-foreground" dir="auto">
                    {team.founded && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {team.founded}
                      </span>
                    )}
                    {team.venue && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {team.venue}
                      </span>
                    )}
                    {team.coach?.name && (
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {team.coach.name}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => favTeams.toggle({
                        id: team.id,
                        name: team.name,
                        shortName: team.shortName,
                        crestUrl: team.crest || null,
                      })}
                      className={cn(
                        'inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border',
                        isFavourite
                          ? 'border-amber-500/40 bg-amber-500/10 text-amber-500'
                          : 'border-input text-muted-foreground hover:text-amber-500',
                      )}
                      dir="auto"
                    >
                      <Star className={cn('w-3 h-3', isFavourite && 'fill-current')} />
                      {t(isFavourite ? 'sports.unfavourite' : 'sports.favourite')}
                    </button>
                    {team.website && (
                      <a
                        href={team.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-input text-muted-foreground hover:text-app-primary-500"
                        dir="ltr"
                      >
                        <Globe className="w-3 h-3" />
                        <span>{t('sports.team.website')}</span>
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              {team.clubColors && (
                <p className="text-[11px] text-muted-foreground mt-3" dir="auto">
                  {t('sports.team.colours')}: {team.clubColors}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Running competitions */}
          {Array.isArray(team.runningCompetitions) && team.runningCompetitions.length > 0 && (
            <Card>
              <CardContent className="p-3 sm:p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-1" dir="auto">
                  {t('sports.team.competitions')}
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {team.runningCompetitions.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted text-foreground"
                      dir="auto"
                    >
                      {c.emblem && (
                        <img src={c.emblem} alt="" loading="lazy" className="w-3 h-3 object-contain" />
                      )}
                      {c.name}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming matches */}
          {upcomingMatches.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-2 px-1" dir="auto">
                {t('sports.team.upcoming')}
              </p>
              <div className="space-y-1.5">
                {upcomingMatches.map((m) => (
                  <TeamMatchRow key={m.id} match={m} locale={locale} t={t} teamId={team.id} />
                ))}
              </div>
            </section>
          )}

          {/* Recent matches */}
          {lastMatches.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-2 px-1" dir="auto">
                {t('sports.team.recent')}
              </p>
              <div className="space-y-1.5">
                {lastMatches.map((m) => (
                  <TeamMatchRow key={m.id} match={m} locale={locale} t={t} teamId={team.id} />
                ))}
              </div>
            </section>
          )}

          {/* Squad */}
          {groupedSquad.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground mb-2 px-1" dir="auto">
                {t('sports.team.squad', { count: team.squad.length })}
              </p>
              <div className="space-y-3">
                {groupedSquad.map(({ section, players }) => (
                  <Card key={section}>
                    <CardContent className="p-3 sm:p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500 mb-2" dir="auto">
                        {sectionLabel(section)}
                      </p>
                      <ul className="divide-y divide-border/30">
                        {players.map((p) => (
                          <li key={p.id} className="flex items-center gap-2 py-1.5 text-xs" dir="auto">
                            <span className="flex-1 min-w-0 text-foreground truncate">{p.name}</span>
                            {p.nationality && (
                              <span className="text-[10px] text-muted-foreground hidden sm:inline" dir="auto">
                                {p.nationality}
                              </span>
                            )}
                            {p.position && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground whitespace-nowrap" dir="auto">
                                {p.position}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

// Compact match row used inside the upcoming / recent sections.
// Links to the full match detail page; shows the opponent + result
// or kickoff time + competition badge. teamId tells us which side
// of the match is "us" so we render the opponent on the right.
function TeamMatchRow({ match, locale, t, teamId }) {
  const isHome = match.homeTeam?.id === teamId
  const opp = isHome ? match.awayTeam : match.homeTeam
  const isFinished = match.status === 'FINISHED' || match.status === 'AWARDED'
  const home = match.score?.fullTime?.home
  const away = match.score?.fullTime?.away
  let resultLabel = null
  let resultTone = 'text-muted-foreground'
  if (isFinished && typeof home === 'number' && typeof away === 'number') {
    resultLabel = `${home} – ${away}`
    const teamScore = isHome ? home : away
    const oppScore = isHome ? away : home
    if (teamScore > oppScore) resultTone = 'text-emerald-500'
    else if (teamScore < oppScore) resultTone = 'text-red-500'
    else resultTone = 'text-amber-500'
  }
  return (
    <RouterLink to={`/sports/${match.id}`} className="block">
      <div className="flex items-center gap-2 p-2 rounded-md border border-border hover:bg-accent transition-colors">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-10 text-center font-mono" dir="ltr">
          {isHome ? t('sports.team.home') : t('sports.team.away')}
        </span>
        {opp?.crest && (
          <img src={opp.crest} alt="" loading="lazy" className="w-5 h-5 object-contain flex-shrink-0" />
        )}
        <span className="flex-1 min-w-0 text-xs text-foreground truncate" dir="auto">
          {opp?.shortName || opp?.name}
        </span>
        {resultLabel ? (
          <span className={cn('text-xs font-mono font-semibold tabular-nums', resultTone)} dir="ltr">
            {resultLabel}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground font-mono" dir="ltr">
            {fmtKickoff(match.utcDate, locale)}
          </span>
        )}
      </div>
    </RouterLink>
  )
}
